import {Component, Inject, OnInit} from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {Product} from "../../../../../model/Product";
import {FacilityInventoryProduct} from "../../../../../model/FacilityInventoryProduct";
import {FacilityInventoryProductService} from "../../../../service-data/facility-inventory-product.service";
import {Organization} from "../../../../../model/Organization";
import {OrganizationService} from "../../../../service-data/organization.service";
import {ProductService} from "../../../../service-data/product.service";
import {StartupService} from "../../../../service-ui/startup.service";

@Component({
  selector: 'app-edit-facility-inventory-product-settings',
  templateUrl: './edit-facility-inventory-product-settings-component.html',
  styleUrls: ['./edit-facility-inventory-product-settings-component.css']
})
export class EditFacilityInventoryProductSettingsComponent implements OnInit {
  public products: Product[];
  public organizations: Organization[];
  public facilityInventoryProduct: FacilityInventoryProduct;
  public formGroup: FormGroup;
  wasFormChanged = false;

  /** product_ID the dialog opened with. Everything about the "did the
   *  operator repoint this shelf?" question is answered by comparing
   *  against this, so it is captured once and never reassigned. */
  private initialProductId: number;

  /** Candidate Products the operator may repoint this shelf at, already
   *  restricted to the owning organization and sorted. Recomputed
   *  whenever the filter text or the show-archived toggle changes. */
  public candidateProducts: Product[] = [];

  /** Free-text filter over candidate title / product id / book id. */
  public productFilter: string = '';

  /** When false (the default) archived Products are omitted from the
   *  picker. Repointing a shelf AT an archived Product is almost never
   *  what is wanted - the usual reason to open this dialog is to move
   *  OFF one - but it is not forbidden, so the toggle exists. */
  public showArchivedProducts: boolean = false;

  /** Parsed FacilityInventoryProduct.productIds, newest first. These are
   *  historical only: they keep old usage records resolving and are
   *  expected to contain archived ids. */
  public historicalProductIds: number[] = [];

  constructor(private formBuilder: FormBuilder, public dialog: MatDialog, public organizationService: OrganizationService, public productService: ProductService, public startupService: StartupService, public facilityInventoryProductService: FacilityInventoryProductService, @Inject(MAT_DIALOG_DATA) data, private dialogRef: MatDialogRef<EditFacilityInventoryProductSettingsComponent>) {
    this.facilityInventoryProduct = data.facilityInventoryProduct;
    this.products = data.products || [];
    for ( const product of this.products) {
      if ( product.ID === this.facilityInventoryProduct.product_ID ) {
        this.facilityInventoryProduct.product = product;
      }
    }
  }

  public ngOnInit(): void {
    this.initialProductId = Number(this.facilityInventoryProduct.product_ID) || 0;
    this.formGroup = this.formBuilder.group({
      product_ID: [this.initialProductId, [Validators.required, Validators.min(1)]],
      state: [this.facilityInventoryProduct.state, [Validators.required]],
      inventoryManagementType: [this.facilityInventoryProduct.inventoryManagementType, [Validators.required]],
      sequence: [this.facilityInventoryProduct.sequence, [Validators.required]],
      reorderQuantity: [this.facilityInventoryProduct.reorderQuantity, [Validators.required]],
      reorderLevel: [this.facilityInventoryProduct.reorderLevel, [Validators.required]],
      location: [this.facilityInventoryProduct.location, [Validators.required]],
      shelf: [this.facilityInventoryProduct.shelf, [Validators.required]],
      caseQuantity: [this.facilityInventoryProduct.caseQuantity, [Validators.required]]
    });

    this.historicalProductIds = this.parseProductIds(this.facilityInventoryProduct.productIds);

    // If the shelf is currently pointed at an archived Product, open the
    // picker with archived entries already visible so the operator can
    // see the row they are moving away from rather than wondering why
    // the current selection is missing from the list.
    this.showArchivedProducts = Product.isArchived(this.facilityInventoryProduct.product);

    this.rebuildCandidateProducts();
  }

  // ---------------------------------------------------------------
  // Product selection
  // ---------------------------------------------------------------

  /** The Product currently chosen in the form (may differ from the
   *  saved one until Save is pressed). */
  public get selectedProduct(): Product {
    return this.getProduct(this.selectedProductId);
  }

  public get selectedProductId(): number {
    return this.formGroup ? Number(this.formGroup.controls.product_ID.value) || 0 : 0;
  }

  /** True when the operator has picked a different Product than the one
   *  the dialog opened with. Drives the "this will be added to the
   *  history" notice and the productIds bookkeeping on save. */
  public get productWasChanged(): boolean {
    return this.selectedProductId !== this.initialProductId;
  }

  /** True when the shelf is (still) pointed at an archived Product. */
  public get selectedProductIsArchived(): boolean {
    return Product.isArchived(this.selectedProduct);
  }

  /** True when the shelf was pointed at an archived Product when the
   *  dialog opened - i.e. the reason the operator is probably here. */
  public get initialProductIsArchived(): boolean {
    return Product.isArchived(this.getProduct(this.initialProductId));
  }

  public get previousProductId(): number {
    return this.initialProductId;
  }

  public isArchived(product: Product): boolean {
    return Product.isArchived(product);
  }

  public isProductIdArchived(productId: number): boolean {
    return Product.isArchived(this.getProduct(productId));
  }

  /**
   * Resolve a product id to a Product, preferring the list handed in by
   * the parent and falling back to the ProductService cache. The parent
   * list only holds Products the inventory screen loaded, so historical
   * ids frequently are not in it.
   */
  public getProduct(productId: number): Product {
    if ( !productId ) {
      return undefined;
    }
    for ( const product of this.products ) {
      if ( product.ID === productId ) {
        return product;
      }
    }
    return this.productService.getLocalItem(productId);
  }

  public getProductTitle(productId: number): string {
    const product = this.getProduct(productId);
    return product ? (product.title || '') : '';
  }

  public onProductFilterChanged(value: string): void {
    this.productFilter = value || '';
    this.rebuildCandidateProducts();
  }

  public setShowArchivedProducts(show: boolean): void {
    this.showArchivedProducts = show;
    this.rebuildCandidateProducts();
  }

  /**
   * Build the picker list.
   *
   * Rules, in order of importance:
   *  1. Same organization only. A shelf's Product must belong to the org
   *     that owns the inventory; offering another org's Product would
   *     produce a replenishment order that cannot be billed.
   *  2. The currently-selected Product is ALWAYS present, even if it is
   *     archived or excluded by the filter text. A mat-select whose
   *     bound value is not among its options renders blank, which reads
   *     as data loss.
   *  3. Archived Products only when the toggle is on, and sorted last.
   */
  private rebuildCandidateProducts(): void {
    const orgId = this.facilityInventoryProduct.product ? this.facilityInventoryProduct.product.org_ID : undefined;
    const filter = this.productFilter.trim().toLowerCase();
    const selectedId = this.selectedProductId;

    const matches = (product: Product): boolean => {
      if ( !product ) { return false; }
      if ( orgId !== undefined && product.org_ID !== orgId ) { return false; }
      if ( !this.showArchivedProducts && Product.isArchived(product) ) { return false; }
      if ( filter.length === 0 ) { return true; }
      const title = (product.title || '').toLowerCase();
      return title.indexOf(filter) > -1 || String(product.ID).indexOf(filter) > -1 || String(product.book_ID).indexOf(filter) > -1;
    };

    const list: Product[] = this.products.filter(matches);

    // Rule 2 - never drop the current selection off the list.
    if ( selectedId && list.findIndex(p => p.ID === selectedId) === -1 ) {
      const selected = this.getProduct(selectedId);
      if ( selected ) {
        list.unshift(selected);
      }
    }

    list.sort((a, b) => {
      const aArchived = Product.isArchived(a) ? 1 : 0;
      const bArchived = Product.isArchived(b) ? 1 : 0;
      if ( aArchived !== bArchived ) {
        return aArchived - bArchived;
      }
      return (a.title || '').localeCompare(b.title || '') || a.ID - b.ID;
    });

    this.candidateProducts = list;
  }

  /**
   * Split FacilityInventoryProduct.productIds into numbers.
   *
   * Tolerant on purpose: legacy rows use ',' and ', ' interchangeably,
   * and some carry empty segments left by earlier
   * append-without-checking code. Anything that is not a positive
   * integer is dropped and duplicates collapse to first occurrence.
   */
  private parseProductIds(productIds: string): number[] {
    if ( !productIds ) {
      return [];
    }
    const result: number[] = [];
    for ( const part of String(productIds).split(',') ) {
      const id = Number(part.trim());
      if ( !isNaN(id) && id > 0 && result.indexOf(id) === -1 ) {
        result.push(id);
      }
    }
    return result;
  }

  // ---------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------

  public onSave(): void {
    const newProductId = this.selectedProductId;
    const newProduct = this.getProduct(newProductId);
    if ( !newProduct ) {
      console.error(`Refusing to save FacilityInventoryProduct ${this.facilityInventoryProduct.ID}: product ${newProductId} could not be resolved`);
      return;
    }

    const objectToUpdate = new FacilityInventoryProduct(undefined);
    objectToUpdate.ID = this.facilityInventoryProduct.ID;
    objectToUpdate.printnode_ID = this.facilityInventoryProduct.printnode_ID;
    objectToUpdate.product_ID = newProduct.ID;
    objectToUpdate.title = newProduct.title;
    objectToUpdate.quantityOnHand = this.facilityInventoryProduct.quantityOnHand;
    objectToUpdate.quantityCommited = this.facilityInventoryProduct.quantityCommited;
    objectToUpdate.lastUpdate = new Date();
    objectToUpdate.state = this.formGroup.controls.state.value;
    objectToUpdate.inventoryManagementType = this.formGroup.controls.inventoryManagementType.value;
    objectToUpdate.sequence = this.formGroup.controls.sequence.value;
    objectToUpdate.reorderQuantity = this.formGroup.controls.reorderQuantity.value;
    objectToUpdate.reorderLevel = this.formGroup.controls.reorderLevel.value;
    objectToUpdate.caseQuantity = this.formGroup.controls.caseQuantity.value;
    objectToUpdate.location = this.formGroup.controls.location.value;
    objectToUpdate.shelf = this.formGroup.controls.shelf.value;

    // Repointing the shelf: the OUTGOING product id is what has to be
    // preserved in productIds, because that is the id every historical
    // usage record for this shelf was written against. (The previous
    // implementation pushed the id it was switching TO, which both lost
    // the history it meant to keep and left the new id recorded twice.)
    //
    // productIds is rewritten wholesale from the parsed list, so this
    // also quietly repairs the empty segments and duplicates left
    // behind by that earlier string concatenation.
    const history = this.parseProductIds(this.facilityInventoryProduct.productIds);
    if ( this.initialProductId && history.indexOf(this.initialProductId) === -1 ) {
      history.unshift(this.initialProductId);
    }
    // The live product_ID does not need to also sit in the history list.
    objectToUpdate.productIds = history.filter(id => id !== newProduct.ID).join(',');

    this.facilityInventoryProductService.updateItem(objectToUpdate).subscribe( object => {
      console.log(`Updated FacilityInventoryProduct ${object.ID} productId: ${object.product_ID}`);
    });
    this.markAsDirty(this.formGroup);
    this.dialogRef.close();
  }

  private markAsDirty(group: FormGroup): void {
    group.markAsDirty();
    // tslint:disable-next-line:forin
    for (const i in group.controls) {
      group.controls[i].markAsDirty();
    }
  }

  formChanged() {
    this.wasFormChanged = true;
  }

}
